import Grid from '@mui/material/Grid'
import { getUserFromLocalStorage } from 'api/apiClient/apiClient'
import i18n from 'i18next'
import React, { ReactElement, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Step } from 'react-joyride'
import { useNavigate } from 'react-router'
import authStore from 'store/auth/auth'
import globalStore from 'store/global/global'
import uiConfigStore from 'store/uiConfig/uiConfig'
import { Toggle } from 'ui/atoms'
import Card from 'ui/atoms/Card/Card'
import NewButton from 'ui/atoms/NewButton/NewButton'
import ContainedIconButton from 'ui/components/ContainedIconButton/ContainedIconButton'
import ModuleHeader from 'ui/components/ModuleHeader/ModuleHeader'
import MyPermissions from 'ui/components/MyPermissions/MyPermissions'
import Tour from 'ui/components/Tour/Tour'
import View from 'ui/components/View/View'
import { useSnapshot } from 'valtio'
import AccountSettings from './components/AccountSettings/AccountSettings'

const widgetConstants = {
  ACCOUNT_WIDGET: `AccountWidget`,
}

const account: Array<Step> = [
  {
    target: widgetConstants.ACCOUNT_WIDGET,
    content: `Here you can view and edit your user information. By security reasons you may not change your email-adress. If you must, please contact us through the contact form above.`,
    title: `Account settings`,
  },
]

export default function UserProfileView(): ReactElement {
  const [runTour, setRunTour] = useState(false)
  const authSnap = useSnapshot(authStore)
  const isSuperUser = authSnap.isSuperUser
  const uiConfigSnap = useSnapshot(uiConfigStore)
  const globalSnap = useSnapshot(globalStore)
  const { t } = useTranslation()
  const navigate = useNavigate()

  return (
    <View
      header={
        <ModuleHeader
          infoHeader={t(`Account settings`)}
          onInfoButtonClick={() => {
            setRunTour(true)
          }}
        />
      }
    >
      <Tour isSingle run={runTour} setRunState={setRunTour} steps={account} />
      <Grid size={{ xs: 12 }}>
        <Card tourId={widgetConstants.ACCOUNT_WIDGET} title={t(`Handle account settings`)} collapsible>
          <AccountSettings />
        </Card>
      </Grid>
      <Grid container sx={{ width: '100%' }}>
        {isSuperUser && (
          <Grid size={{ xs: 12, md: 6 }}>
            <Card collapsible title={t('Super user settings')}>
              <div className="flex flex-col gap-2">
                <Toggle
                  label={t('Show Ui Config debug menu')}
                  value={uiConfigSnap.showDebugMenu}
                  onClick={(value) => {
                    uiConfigStore.showDebugMenu = value
                  }}
                />
                <Toggle
                  label={t('Open system menu with shortcut (s-key)')}
                  value={globalSnap.systemShortcut}
                  onClick={(value) => {
                    globalStore.systemShortcut = value
                  }}
                />
                <div>
                  <ContainedIconButton
                    color="primary"
                    icon="fal fa-user-secret"
                    label={t('Impersonate user')}
                    onClick={() => navigate('/impersonate')}
                  />
                </div>
                <div>
                  <ContainedIconButton
                    color="primary"
                    icon="fal fa-copy"
                    label={t('Copy my API token to clipboard')}
                    onClick={() => {
                      const user = getUserFromLocalStorage()
                      navigator.clipboard.writeText(user?.token || '')
                    }}
                  />
                </div>
              </div>
            </Card>
          </Grid>
        )}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card collapsible title={t(`About`)} titleSize="medium">
            <div className="flex flex-col gap-2">
              <p>
                <strong>{t(`Application`)}:</strong> Aurora by Sigholm®
              </p>
              <NewButton
                label={t(`Data Protection Policy`)}
                icon="fal fa-file-pdf"
                onClick={() =>
                  window.open(`/pdf/dataprotectionpolicy_${i18n.language || 'en'}.pdf`, '_blank', 'noreferrer noopener')
                }
              />
            </div>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Card collapsible defaultCollapsed title={t(`Your permissions on this system:`)} titleSize="medium">
            <MyPermissions />
          </Card>
        </Grid>
      </Grid>
    </View>
  )
}
